import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import useReactHookFormStyles from '../../../../custom-components/form-components/ReactHookForm.styles';
import { getHelperTextFromConfig } from '../utils';
import { Box, Typography } from '@mui/material';
import { generateTestId } from '../../../../../utils';

const FormInput = ({
    name,
    props: {
        label,
        rules,
        fieldOpts,
        placeholder,
        required,
        type,
        currency,
        InputProps,
        defaultValue,
        valueAsNumber,
        extraHelperText,
        nullOverride,
        shouldUnregister, // Field should unregister if no longer rendered
        subtitle, // small text displayed below the input
        ...rest
    },
}) => {
    const classes = useReactHookFormStyles();

    const {
        control,
        formState: { errors },
        getValues,
        setValue,
    } = useFormContext();

    // if name is dot notation for nested values, access the nested value on the error object
    const nestedName = name.split('.');
    // by default use errors[name]
    const error = errors[name] || (nestedName.length && nestedName.reduce((acc, key) => acc?.[key], errors));

    const helperText = getHelperTextFromConfig(extraHelperText, getValues);

    useEffect(() => {
        if (type === 'number' && valueAsNumber !== false) {
            const val = getValues(name);
            if (val) setValue(name, parseFloat(val));
        }
    }, [type]);

    return (
        <FormControl className={type !== 'hidden' ? classes.formController : classes.hideForm} style={{ height: 56 }}>
            <Controller
                name={name}
                control={control}
                rules={rules}
                defaultValue={defaultValue}
                shouldUnregister
                render={({ field: { onChange, value, ...controllerField } }) => (
                    <TextField
                        type={type}
                        placeholder={placeholder}
                        value={nullOverride && value === null ? defaultValue : value === 0 ? value : value || null}
                        onChange={(event) => {
                            const val = valueAsNumber ? parseFloat(event.target.value) : event.target.value;
                            onChange(nullOverride && val === null ? defaultValue : val === 0 ? val : val || null);
                        }}
                        InputProps={{
                            ...InputProps,
                            ...(currency && {
                                startAdornment: (
                                    <InputAdornment position="start" data-testid={generateTestId(name, 'fi-adornment')}>
                                        {currency}
                                    </InputAdornment>
                                ),
                            }),
                        }}
                        InputLabelProps={{ shrink: true }}
                        style={{ width: '100%' }}
                        {...controllerField}
                        {...fieldOpts}
                        error={!!error}
                        required={required}
                        label={label}
                        helperText={
                            <>
                                {error?.message}
                                {error?.message && helperText && <br />}
                                {helperText}
                            </>
                        }
                        data-testid={generateTestId(name, 'fi-textfield')}
                        {...rest}
                    />
                )}
            />
            {subtitle && (
                <Box padding={0.5}>
                    <Typography variant="body2" color="textSecondary" data-testid={generateTestId(name, 'fi-subtitle')}>
                        {subtitle}
                    </Typography>
                </Box>
            )}
        </FormControl>
    );
};

export default FormInput;
